import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Film } from 'lucide-react';

// Give up after this many fresh links in a row that didn't get playback going.
const MAX_REFRESHES = 3;

/**
 * Video player for the file viewer (OL-165).
 *
 * Links to a file are short-lived (60 seconds on the recipient page), and the
 * browser keeps requesting byte ranges while a video plays or seeks. A request
 * made after the link has expired fails, so on an error mid-playback this asks
 * for a fresh link and picks up where it left off. An error before anything
 * has loaded means the browser can't play this video at all.
 *
 * @param src            the current link
 * @param onRefresh      async () => a fresh link
 * @param allowDownload  false on a View Only link (OL-166): hides the player's
 *                       download control and the right-click "Save video as"
 */
const VideoPlayer = ({ src, title, onRefresh, allowDownload = true }) => {
  const videoRef = useRef(null);
  const [currentSrc, setCurrentSrc] = useState(src);
  const [unplayable, setUnplayable] = useState(false);
  const loaded = useRef(false);
  const playing = useRef(false);
  const refreshes = useRef(0);
  const resumeAt = useRef(null); // { time, play }
  // The element can report 0 by the time the error fires, so keep our own copy.
  const lastTime = useRef(0);

  useEffect(() => {
    setCurrentSrc(src);
    setUnplayable(false);
    loaded.current = false;
    refreshes.current = 0;
    resumeAt.current = null;
    lastTime.current = 0;
  }, [src]);

  const onError = async () => {
    const video = videoRef.current;
    if (!video || !loaded.current || !onRefresh || refreshes.current >= MAX_REFRESHES) {
      setUnplayable(true);
      return;
    }

    refreshes.current += 1;
    resumeAt.current = { time: video.currentTime || lastTime.current, play: playing.current };
    try {
      setCurrentSrc(await onRefresh());
    } catch {
      setUnplayable(true);
    }
  };

  const onLoadedMetadata = () => {
    loaded.current = true;
    const video = videoRef.current;
    if (!video || !resumeAt.current) return;

    video.currentTime = resumeAt.current.time;
    if (resumeAt.current.play) video.play().catch(() => {});
    resumeAt.current = null;
  };

  if (unplayable) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-sm text-gray-600">
        <Film size={40} className="text-gray-400" />
        <p>This video can&apos;t be played in the browser.</p>
      </div>
    );
  }

  return (
    <video
      ref={videoRef}
      src={currentSrc}
      title={title}
      controls
      controlsList={allowDownload ? undefined : 'nodownload'}
      onContextMenu={allowDownload ? undefined : (event) => event.preventDefault()}
      autoPlay
      playsInline
      preload="metadata"
      className="max-w-full"
      style={{ maxHeight: '75vh' }}
      onLoadedMetadata={onLoadedMetadata}
      onPlay={() => {
        playing.current = true;
      }}
      onPause={() => {
        playing.current = false;
      }}
      onTimeUpdate={() => {
        const video = videoRef.current;
        // Ignore the reset to 0 while a source is (re)loading.
        if (!video || resumeAt.current || video.readyState < 1) return;
        lastTime.current = video.currentTime;
        // Playback is moving again, so the next expiry gets a fresh set of tries.
        refreshes.current = 0;
      }}
      onError={onError}
    />
  );
};

VideoPlayer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string,
  onRefresh: PropTypes.func,
  allowDownload: PropTypes.bool,
};

export { VideoPlayer };
